import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function IntroAnimation({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(timer);
          setTimeout(() => setIsExiting(true), 1200);
          setTimeout(onComplete, 2800);
          return 100;
        }
        return prev + 1.5;
      });
    }, 40);
    return () => clearInterval(timer);
  }, [onComplete]);

  return (
    <AnimatePresence>
      {!isExiting && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.05, filter: "blur(15px)" }}
          transition={{ duration: 1.8, ease: [0.43, 0.13, 0.23, 0.96] }}
          className="fixed inset-0 z-[200] bg-brand-light flex flex-col items-center justify-center overflow-hidden"
        >
          {/* Organic Grain Texture Overlay */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-multiply" style={{ backgroundImage: `url('https://www.transparenttextures.com/patterns/felt.png')` }} />
          
          {/* Floating Bougainvillea Petals (Boho Vibe) */}
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ 
                x: Math.random() * 100 - 50 + "%", 
                y: "110%", 
                rotate: 0,
                opacity: 0 
              }}
              animate={{ 
                y: "-10%", 
                rotate: 360,
                opacity: [0, 0.4, 0]
              }}
              transition={{ 
                duration: 6 + Math.random() * 4, 
                repeat: Infinity, 
                delay: i * 0.8,
                ease: "linear"
              }}
              className="absolute w-4 h-4 rounded-full bg-brand-accent/30 blur-[1px]"
            />
          ))}

          {/* Tropical Leaf Silhouettes */}
          <motion.div 
            initial={{ opacity: 0, rotate: -10 }}
            animate={{ opacity: 0.05, rotate: 0 }}
            transition={{ duration: 3 }}
            className="absolute -bottom-20 -left-20 w-96 h-96 pointer-events-none"
          >
            <svg viewBox="0 0 200 200" fill="currentColor" className="text-brand-dark">
              <path d="M100,20 C120,50 150,70 180,100 C150,130 120,150 100,180 C80,150 50,130 20,100 C50,70 80,50 100,20" />
            </svg>
          </motion.div>

          <div className="relative z-20 flex flex-col items-center max-w-xs w-full px-6">
            {/* Elegant Sun/Compass Motif */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 2 }}
              className="mb-12 relative"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                className="w-32 h-32 border border-brand-accent/20 rounded-full flex items-center justify-center"
              >
                {[...Array(12)].map((_, i) => (
                  <div 
                    key={i} 
                    className="absolute w-full h-[1px] bg-brand-accent/10" 
                    style={{ transform: `rotate(${i * 30}deg)` }} 
                  />
                ))}
              </motion.div>
              <div className="absolute inset-0 flex items-center justify-center font-serif text-brand-accent text-3xl italic">
                J&B
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 1 }}
              className="text-center"
            >
              <h2 className="font-serif text-brand-dark text-xl tracking-[0.2em] mb-3 uppercase">
                Bienvenidos
              </h2>
              <p className="font-sans text-[10px] text-brand-accent tracking-[0.4em] uppercase font-bold mb-12">
                Cartagena de Indias
              </p>
            </motion.div>

            {/* Elegant Minimal Progress */}
            <div className="w-full h-[1px] bg-brand-muted/30 relative">
              <motion.div 
                className="absolute top-0 left-0 h-full bg-brand-accent"
                initial={{ width: "0%" }}
                animate={{ width: `${progress}%` }}
              />
            </div>
            
            <div className="mt-6 font-serif italic text-sm text-brand-dark/40 tracking-widest">
              {Math.round(progress)}%
            </div>
          </div>

          {/* Soft Corner Accents */}
          <div className="absolute top-12 left-12 w-12 h-12 border-l border-t border-brand-accent/20 rounded-tl-3xl" />
          <div className="absolute bottom-12 right-12 w-12 h-12 border-r border-b border-brand-accent/20 rounded-br-3xl" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}